import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["ltvInput", "communitySelect", "maxLtvInfo", "warningMessage", "premiumInfo"]

  connect() {
    this.validateLTV()
  }

  communityChanged() {
    this.validateLTV()
  }

  ltvChanged() {
    this.validateLTV()
  }

  validateLTV() {
    if (!this.hasLtvInputTarget || !this.hasCommunitySelectTarget) return

    const selectedOption = this.communitySelectTarget.options[this.communitySelectTarget.selectedIndex]
    if (!selectedOption || selectedOption.value === "") {
      this.hideAllMessages()
      return
    }

    const communityMaxLtv = parseFloat(selectedOption.dataset.maxLtv)
    const enteredLtv = parseFloat(this.ltvInputTarget.value)

    if (isNaN(communityMaxLtv)) {
      this.hideAllMessages()
      return
    }

    // Show community max LTV info
    if (this.hasMaxLtvInfoTarget) {
      this.maxLtvInfoTarget.innerHTML = `
        <div class="badge badge-info gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          Community Max LTV: ${communityMaxLtv}%
        </div>
      `
      this.maxLtvInfoTarget.classList.remove("hidden")
    }

    // Check if entered LTV exceeds community max
    if (!isNaN(enteredLtv) && enteredLtv > communityMaxLtv) {
      const excessLtv = enteredLtv - communityMaxLtv
      const minPremium = 1.0 // Minimum 1% rate premium

      // Show warning message
      if (this.hasWarningMessageTarget) {
        this.warningMessageTarget.innerHTML = `
          <div class="alert alert-warning">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <div class="font-bold">LTV Exceeds Community Maximum</div>
              <div class="text-sm">Your LTV of ${enteredLtv}% exceeds the community maximum of ${communityMaxLtv}%.</div>
              <div class="text-sm mt-1">This loan may require additional review and approval.</div>
            </div>
          </div>
        `
        this.warningMessageTarget.classList.remove("hidden")
      }

      // Show premium info
      if (this.hasPremiumInfoTarget) {
        this.premiumInfoTarget.innerHTML = `
          <div class="alert alert-info">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <div>
              <div class="font-bold">Rate Premium Applied</div>
              <div class="text-sm">A minimum rate premium of <span class="font-bold">+${minPremium}%</span> will be added to your base rate.</div>
              <div class="text-sm text-gray-600 mt-1">Actual premium may be higher based on risk assessment.</div>
            </div>
          </div>
        `
        this.premiumInfoTarget.classList.remove("hidden")
      }
    } else {
      // Hide warning and premium if LTV is within limits
      this.hideWarningAndPremium()
    }
  }

  hideAllMessages() {
    if (this.hasMaxLtvInfoTarget) {
      this.maxLtvInfoTarget.classList.add("hidden")
    }
    this.hideWarningAndPremium()
  }

  hideWarningAndPremium() {
    if (this.hasWarningMessageTarget) {
      this.warningMessageTarget.classList.add("hidden")
    }
    if (this.hasPremiumInfoTarget) {
      this.premiumInfoTarget.classList.add("hidden")
    }
  }
}